/**
 * Dashboard Hook
 *
 * Fetches all dashboard statistics from the API.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type { QuoteQueueCounts, QuoteQueueItem } from './useQuoteQueue';
import type { BookingStatus } from '@/types';

export interface DashboardMetrics {
  todaysBookings: number;
  activeWorkOrders: number;
  pendingApprovals: number;
  todaysRevenueCents: number;
  myWorkOrders: number;
  completedToday: number;
  hoursWorkedToday: number;
  pendingParts: number;
}

export interface RevenueData {
  date: string;
  /** Whole euros — kept for the mechanic app; use `revenueCents`. */
  revenue: number;
  revenueCents: number;
  target: number;
}

export type RevenueCategory = 'consumer' | 'company' | 'cashCard';

export interface RevenueMonth {
  /** `YYYY-MM` */
  month: string;
  revenueCents: number;
  byCategory: Record<RevenueCategory, number>;
}

/** Invoiced net revenue (#3906): window total and the current year by month. */
export interface RevenueSummary {
  /** Tenant-local today, `YYYY-MM-DD`. */
  asOf: string;
  windowDays: number;
  totalCents: number;
  /** One entry per month of the current year, January first. */
  year: RevenueMonth[];
}

export interface StatusData {
  name: string;
  value: number;
  status: BookingStatus;
}

export interface ScheduleItem {
  id: string;
  time: string;
  customerName: string;
  vehicleRegNumber: string;
  vehicleMake: string | null;
  service: string;
  mechanicName: string;
  status: BookingStatus;
}

export interface MechanicUtilization {
  name: string;
  utilization: number;
  hoursWorked: number;
  hoursAvailable: number;
}

export type ActivityType =
  | 'booking_created'
  | 'work_started'
  | 'work_completed'
  | 'approval_requested'
  | 'approval_received'
  | 'parts_ordered'
  | 'customer_arrived';

export interface Activity {
  id: string;
  type: ActivityType;
  time: string;
  meta?: {
    serviceType?: string;
    vehicleRegNumber?: string;
    vehicleId?: string | null;
    workOrderNumber?: string | null;
    customerName?: string;
    mechanicName?: string;
    /** User behind the latest status change (#3728); empty when unknown. */
    actorName?: string;
    /** Actor's user id, links the name to the team member in HR (#3937). */
    actorId?: string | null;
  };
}

export type AlertSeverity = 'critical' | 'warning' | 'info';
export type AlertType =
  | 'low_inventory'
  | 'overdue_approval'
  | 'overdue_work_order'
  | 'unconfirmed_booking'
  | 'equipment_maintenance';

export interface DashboardAlert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  /** i18n key (dashboard namespace) for the alert title. */
  titleKey: string;
  titleParams?: Record<string, string | number>;
  /** i18n key for the description, when it is not raw domain data. */
  descriptionKey?: string;
  descriptionParams?: Record<string, string | number>;
  /** Raw (non-localized) description, e.g. a comma-joined list of part names. */
  descriptionText?: string;
  link?: string;
  count?: number;
}

export interface PartsArrivingItem {
  bookingId: string;
  customerName: string;
  vehicleRegNumber: string;
  expectedDeliveryDate: string;
  poOrderNumber: string;
  supplierName: string;
  parts: Array<{ partName: string; quantityOrdered: number; quantityReceived: number }>;
}

/** Widget: uninvoiced completed work (total + top-N list). */
export interface UninvoicedItem {
  bookingId: string;
  customerName: string;
  vehicleRegNumber: string;
  totalCents: number;
  completedAt: string | null;
}
export interface UninvoicedSummary {
  count: number;
  totalCents: number;
  items: UninvoicedItem[];
}

/** Widget: a job blocked waiting for parts, with the linked PO's ETA if any. */
export interface PartsBlockedItem {
  bookingId: string;
  serviceType: string;
  customerName: string;
  vehicleRegNumber: string;
  /** ISO date of the linked PO's expected delivery, or null if not ordered. */
  expectedDeliveryDate: string | null;
  poOrderNumber: string | null;
  partName: string | null;
  hasOrder: boolean;
}

/** Widget: an open approval request awaiting the customer, with its age. */
export interface AwaitingCustomerItem {
  id: string;
  bookingId: string | null;
  customerName: string;
  vehicleRegNumber: string;
  description: string;
  totalCents: number;
  urgency: string | null;
  /** ISO timestamp the request was created — client computes age from this. */
  createdAt: string | null;
}

/** Widget: per-mechanic capacity for today (available vs committed vs logged). */
export interface MechanicCapacity {
  userId: string;
  name: string;
  availableHours: number;
  committedHours: number;
  loggedHours: number;
  utilizationPct: number;
}
export interface CapacitySummary {
  /** False when no shift patterns are configured (available hours not real). */
  shiftsConfigured: boolean;
  date: string;
  mechanics: MechanicCapacity[];
  totalAvailableHours: number;
  totalCommittedHours: number;
}

/** Widget: the quote queue's bucket counts and the next quotes to act on (#2652). */
export interface QuoteQueueSummary {
  counts: QuoteQueueCounts;
  items: QuoteQueueItem[];
}

export interface DashboardStats {
  metrics: DashboardMetrics;
  revenue: RevenueData[];
  revenueSummary?: RevenueSummary;
  workOrderStatuses: StatusData[];
  todaySchedule: ScheduleItem[];
  mechanicUtilization: MechanicUtilization[];
  recentActivity: Activity[];
  alerts: DashboardAlert[];
  partsArriving?: PartsArrivingItem[];
  uninvoiced?: UninvoicedSummary;
  partsBlocked?: PartsBlockedItem[];
  awaitingCustomer?: AwaitingCustomerItem[];
  capacity?: CapacitySummary | null;
  quoteQueue?: QuoteQueueSummary | null;
}

export function useDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard.stats(),
    queryFn: () => api.get<DashboardStats>('/dashboard/stats'),
    refetchInterval: 60_000,
  });
}
